import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  TextField,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  FormControl,
  Select,
  useMediaQuery,
  useTheme,
  Button,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Grid,
  Drawer,
} from "@mui/material";
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { DataGrid } from "@mui/x-data-grid";
import { getAllProducts, deleteProduct } from "../../service/productService";
import { getAllBrandLists } from "../../service/brandService";
import { getAllCategoryLists } from "../../service/categoryService";
import { useSnackbar } from "../../App";
import AddProduct from "./AddProduct";
import EditProduct from "./EditProduct";
import Color from "../../components/Color";

const Products = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isExtraSmall = useMediaQuery(theme.breakpoints.down("xs"));
  const { showSnackbar } = useSnackbar();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [rowCount, setRowCount] = useState(0);
  const [searchProducts, setSearchProducts] = useState("");
  const [productCodeSearch, setProductCodeSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [debouncedProductCode, setDebouncedProductCode] = useState("");
  const [brandFilter, setBrandFilter] = useState("All");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [sortModel, setSortModel] = useState([{ field: "id", sort: "desc" }]);

  const menuOpen = Boolean(anchorEl);

  const handleMenuOpen = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const EditModal = (id) => {
    setSelectedId(id);
    setOpenEditModal(true);
  };

  const fetchProducts = useCallback(
    async (opts = {}) => {
      setLoading(true);
      try {
        const page = (opts.page ?? paginationModel.page) + 1;
        const limit = opts.pageSize ?? paginationModel.pageSize;
        const sortField = sortModel[0]?.field || "id";
        const sortOrder = sortModel[0]?.sort || "desc";

        const res = await getAllProducts(
          page,
          limit,
          debouncedSearch || "",
          debouncedProductCode || "",
          brandFilter !== "All" ? brandFilter : "",
          categoryFilter !== "All" ? categoryFilter : "",
          statusFilter !== "All" ? statusFilter : "",
          sortField,
          sortOrder
        );

        const payload = res?.data;
        let dataArray = [];
        let total = 0;

        if (!payload) {
          dataArray = [];
          total = 0;
        } else if (Array.isArray(payload.data)) {
          dataArray = payload.data;
          total = payload.total ?? dataArray.length;
        } else if (payload.data && Array.isArray(payload.data.data)) {
          dataArray = payload.data.data;
          total = payload.data.total ?? dataArray.length;
        } else if (Array.isArray(payload)) {
          dataArray = payload;
          total = payload.length;
        } else {
          dataArray = payload.data || payload.items || [];
          if (!Array.isArray(dataArray)) dataArray = [];
          total = payload.total ?? (Array.isArray(dataArray) ? dataArray.length : 0);
        }

        const formattedRows = dataArray.map((item) => ({
          ...item,
          brandName: item?.brand?.name ?? "--",
          categoryName: item?.category?.name ?? "--",
          image_url: item?.image_url ?? "",
        }));

        setRows(formattedRows);
        setRowCount(Number(total) || formattedRows.length);
      } catch (error) {
        showSnackbar("Failed to fetch products!", "error");
        setRows([]);
        setRowCount(0);
      } finally {
        setLoading(false);
      }
    },
    [
      paginationModel.page,
      paginationModel.pageSize,
      debouncedSearch,
      debouncedProductCode,
      brandFilter,
      categoryFilter,
      statusFilter,
      sortModel,
      showSnackbar,
    ]
  );

  useEffect(() => {
    fetchProducts({ page: paginationModel.page, pageSize: paginationModel.pageSize });
  }, [
    fetchProducts,
    paginationModel.page,
    paginationModel.pageSize,
    debouncedSearch,
    debouncedProductCode,
    brandFilter,
    categoryFilter,
    statusFilter,
    sortModel,
  ]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
      setDebouncedSearch(searchProducts);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchProducts]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
      setDebouncedProductCode(productCodeSearch);
    }, 500);
    return () => clearTimeout(handler);
  }, [productCodeSearch]);

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const [brandsRes, categoriesRes] = await Promise.all([
          getAllBrandLists(),
          getAllCategoryLists(),
        ]);
        setBrands(brandsRes?.data?.data ?? brandsRes?.data ?? []);
        setCategories(categoriesRes?.data?.data ?? categoriesRes?.data ?? []);
      } catch (err) {
        showSnackbar("Failed to load brand/category filters", "error");
      }
    };
    fetchFilters();
  }, [showSnackbar]);

  const clearFilters = () => {
    setSearchProducts("");
    setProductCodeSearch("");
    setDebouncedSearch("");
    setDebouncedProductCode("");
    setBrandFilter("All");
    setCategoryFilter("All");
    setStatusFilter("All");
    setPaginationModel({ page: 0, pageSize: 10 });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setDebouncedSearch(searchProducts);
    setDebouncedProductCode(productCodeSearch);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setDrawerOpen(false);
  };

  const handleReset = () => {
    clearFilters();
    setDrawerOpen(true);
  };

  const handleDeleteClick = (id) => {
    if (!id) {
      showSnackbar("No product selected!", "error");
      return;
    }
    setDeleteId(id);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteProduct(deleteId);
      showSnackbar("Product deleted successfully!", "success");
      fetchProducts({ page: paginationModel.page, pageSize: paginationModel.pageSize });
    } catch (error) {
      showSnackbar(error?.response?.data?.message || "Failed to delete product!", "error");
    } finally {
      setDeleteDialogOpen(false);
      setDeleteId(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setDeleteId(null);
  };

  const columns = [
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.5,
      sortable: false,
      minWidth: isMobile ? 80 : 120,
      renderCell: (params) => (
        <IconButton
          onClick={(e) => handleMenuOpen(e, params.row)}
          size="small"
          sx={{
            backgroundColor: "rgba(0,0,0,0.04)",
            borderRadius: 1.5,
            padding: isMobile ? "10px" : "8px",
            "&:hover": {
              backgroundColor: "rgba(0,0,0,0.08)",
            },
            alignItems:'center'
          }}
        >
          <MoreVertIcon sx={{ fontSize: isMobile ? 18 : 16 }} />
        </IconButton>
      ),
    },
    {
  field: "image_url",
  headerName: "Image",
  flex: .7,
  minWidth: isMobile ? 100 : 120,
  renderCell: (params) => (
    
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "25%",
          height: "100%",
        }}
      >
        <img
          src={params.value || "/placeholder.png"} 
        alt={params.row.name || "No Image"}
          style={{
            width: isMobile ? 35 : 40,
            height: isMobile ? 35 : 40,
            objectFit: "cover",
            borderRadius: "6px",
            border: '2px solid lightgray'
          }}
        />
      </Box>
    ) 
},

    { field: "product_code", headerName: "Product Code", flex: 1, minWidth: isMobile ? 150 : 150 },
    { field: "name", headerName: "Product Name", flex: 1.2, minWidth: isMobile ? 150 : 150 },
    { field: "brandName", headerName: "Brand", flex: 1, minWidth: isMobile ? 100 : 150 },
    { field: "categoryName", headerName: "Category", flex: 1, minWidth: 150 },
    { field: "base_price", headerName: "Base Price", flex: 1, minWidth: 120 },
    {
      field: "is_active",
      headerName: "Status",
      flex: 1,
      minWidth: isMobile ? 100 : 150,
      renderCell: (params) => (
        <Chip
          label={params.value ? "Active" : "Inactive"}
          color={params.value ? "success" : "error"}
          size="small"
          sx={{ fontSize: isMobile ? "0.75rem" : "0.875rem" }}
        />
      ),
    },
  ];

  return (
    <Box mb={{ xs: 0, sm: 1 }} sx={{ padding: isMobile ? 1 : 0 }}>
      <Box
        display="flex"
        flexDirection={isMobile ? "column" : "row"}
        justifyContent="space-between"
        alignItems={isMobile ? "stretch" : "center"}
        flexWrap="wrap"
        gap={isMobile ? 1 : 2}
        mt={2}
      >
        <TextField
          value={searchProducts}
          onChange={(e) => setSearchProducts(e.target.value)}
          placeholder="Search product"
          variant="outlined"
          InputLabelProps={{ shrink: false }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: isMobile ? 18 : 20 }} />
              </InputAdornment>
            ),
            endAdornment: searchProducts && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => {
                    setSearchProducts("");
                    setDebouncedSearch("");
                  }}
                >
                  <CloseIcon sx={{ fontSize: isMobile ? 16 : 18 }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiInputLabel-root": { display: "none" },
            "& .MuiOutlinedInput-root": {
              "&.Mui-focused fieldset": { borderColor: "#7C0BCC" },
              height: isMobile ? "34px" : "37.69px",
              fontSize: isMobile ? "0.85rem" : "1rem",
            },
            width: isMobile ? "100%" : 250,
            mb: isMobile ? 0 : 0,
          }}
        />
        <Box sx={{ display: "flex", alignItems: "center", gap: isMobile ? 1 : 2 }}>
          <IconButton
            sx={{
              color: Color.secondary.main,
              fontSize: isMobile ? "24px" : "30px",
              border: "1px solid",
              borderColor: Color.secondary.main,
              borderRadius: "4px",
              height: isMobile ? "34px" : "37px",
              width: isMobile ? "34px" : "37px",
            }}
            onClick={() => setDrawerOpen(true)}
            size="small"
          >
            <FilterAltIcon sx={{ fontSize: isMobile ? 20 : 24 }} />
          </IconButton>
          <Button
            sx={{
              background: Color.primary.main,
              color: "white",
              height: isMobile ? "34px" : "37.69px",
              width: isMobile ? "100%" : 150,
              fontSize: isMobile ? "0.85rem" : "1rem",
              "&:hover": {
                background: Color.primary.dark,
              },
            }}
            onClick={() => setOpenAddModal(true)}
            startIcon={<AddIcon />}
            size="medium"
          >
            Add Product
          </Button>
        </Box>
      </Box>

      <Box sx={{ width: "100%", mt: isMobile ? 2 : 2 }}>
        <DataGrid
  rows={rows}
  columns={columns.map((col) => ({
    ...col,
    flex: col.flex ?? 1,
    minWidth: col.minWidth ?? (isMobile ? 80 : 100),
  }))}
  paginationModel={paginationModel}
  onPaginationModelChange={(model) => {
    setPaginationModel(model);
  }}
  rowCount={rowCount}
  rowHeight={isMobile ? 48 : 56}
  paginationMode="server"
  sortModel={sortModel}
  onSortModelChange={(newModel) => {
    const defaultSort = [{ field: "id", sort: "desc" }];
    const finalModel = newModel.length ? newModel : defaultSort;
    if (JSON.stringify(finalModel) !== JSON.stringify(sortModel)) {
      setSortModel(finalModel);
    }
  }}
  sortingMode="server"
  getRowId={(row) => row.id}
  loading={loading}
  disableRowSelectionOnClick
  autoHeight
  sx={{
    fontFamily: '"Poppins", sans-serif',
    borderRadius: "10px",
    "& .MuiDataGrid-columnHeaderTitle": {
      fontSize: isMobile ? "14px" : "18px",
      fontWeight: 500,
      pl: 1,
    },
    "& .MuiDataGrid-cell": {
      fontSize: isMobile ? "12px" : "14px",
      pl: 2,
    },
    "& .MuiDataGrid-row:nth-of-type(even)": {
      backgroundColor: "#fff",
    },
    "& .MuiDataGrid-row:nth-of-type(odd)": {
      backgroundColor: "#f4f4f4",
    },
    "& .MuiLinearProgress-bar": {
      backgroundColor: Color.primary.main,
    },
    "& .MuiLinearProgress-colorPrimary": {
      backgroundColor: Color.secondary.main,
    },
    overflowX: isMobile ? "hidden" : "auto",

    
    "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
      outline: "none",
      border: "2px solid",
      borderColor: Color.primary.main,
      borderRadius: "4px",
    },

  
    "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
      outline: "none",
      border: "2px solid",
      borderColor: Color.primary.main,
      borderRadius: "4px",
    },
  }}
/>

      </Box>

      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        PaperProps={{
          style: {
            maxHeight: 200,
            width: isMobile ? "90vw" : 200,
          },
        }}
      >
        <MenuItem
          onClick={() => {
            EditModal(selectedRow?.id);
            handleMenuClose();
          }}
          sx={{ fontSize: isMobile ? "0.85rem" : "1rem", padding: isMobile ? "10px" : "8px" }}
        >
          <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleDeleteClick(selectedRow?.id);
          }}
          sx={{ fontSize: isMobile ? "0.85rem" : "1rem", padding: isMobile ? "10px" : "8px" }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1, color: "error.main" }} /> Delete
        </MenuItem>
      </Menu>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            setDrawerOpen(false);
          }
        }}
        PaperProps={{
          sx: {
            width: isMobile ? "60vw" : 420,
            boxShadow: 4,
            borderRadius: "8px 0 0 8px",
          },
        }}
        sx={{
          zIndex: 1300,
        }}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(129, 128, 128, 0.05)",
            backdropFilter: "blur(2px)",
          },
        }}
      >
        <Box
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          sx={{
            fontFamily: '"Poppins", sans-serif',
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            px={2}
            py={1}
            mt={isMobile ? 1 : 2}
            borderBottom="1px solid #e0e0e0"
          >
            <Typography fontSize={isMobile ? "1rem" : 18} fontWeight={600}>
              Filter Products
            </Typography>
            <IconButton onClick={() => setDrawerOpen(false)} size="small">
              <CloseIcon sx={{ fontSize: isMobile ? 20 : 24 }} />
            </IconButton>
          </Box>

          <Box p={isMobile ? 1 : 2} sx={{ flexGrow: 1, overflowY: "auto" }}>
            <form onSubmit={handleSearch}>
              <Grid container spacing={isMobile ? 1 : 2}>
                <Grid item xs={12}>
                  <TextField
                    value={searchProducts}
                    onChange={(e) => setSearchProducts(e.target.value)}
                    placeholder=" product name"
                    variant="outlined"
                    fullWidth
                    InputLabelProps={{ shrink: false }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ fontSize: isMobile ? 18 : 20 }} />
                        </InputAdornment>
                      ),
                      endAdornment: searchProducts && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSearchProducts("");
                              setDebouncedSearch("");
                            }}
                          >
                            <CloseIcon sx={{ fontSize: isMobile ? 16 : 18 }} />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiInputLabel-root": { display: "none" },
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": { borderColor: "#7C0BCC" },
                        height: isMobile ? "34px" : "37.69px",
                        fontSize: isMobile ? "0.85rem" : "1rem",
                      },
                      width: "100%",
                      width: isMobile ? "100%" : 185,
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    value={productCodeSearch}
                    onChange={(e) => setProductCodeSearch(e.target.value)}
                    placeholder="product code"
                    variant="outlined"
                    fullWidth
                    InputLabelProps={{ shrink: false }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ fontSize: isMobile ? 18 : 20 }} />
                        </InputAdornment>
                      ),
                      endAdornment: productCodeSearch && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setProductCodeSearch("");
                              setDebouncedProductCode("");
                            }}
                          >
                            <CloseIcon sx={{ fontSize: isMobile ? 16 : 18 }} />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiInputLabel-root": { display: "none" },
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": { borderColor: "#7C0BCC" },
                        height: isMobile ? "34px" : "37.69px",
                        fontSize: isMobile ? "0.85rem" : "1rem",
                      },
                      width: isMobile ? "100%" : 185,
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <Select
                      value={brandFilter}
                      onChange={(e) => setBrandFilter(e.target.value)}
                      displayEmpty
                      sx={{
                        height: isMobile ? "34px" : "37.69px",
                        fontSize: isMobile ? "0.85rem" : "1rem",
                        width: isMobile ? "153%" : 185,
                      }}
                    >
                      <MenuItem value="All">Select Brand</MenuItem>
                      {brands.map((brand) => (
                        <MenuItem key={brand.id} value={brand.id}>
                          {brand.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <Select
                      value={categoryFilter}
                      onChange={(e) => setCategoryFilter(e.target.value)}
                      displayEmpty
                      sx={{
                        height: isMobile ? "34px" : "37.69px",
                        fontSize: isMobile ? "0.85rem" : "1rem",
                        width: isMobile ? "130%" : 185,
                      }}
                    >
                      <MenuItem value="All">Select Category</MenuItem>
                      {categories.map((cat) => (
                        <MenuItem key={cat.id} value={cat.id}>
                          {cat.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <Select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      displayEmpty
                      sx={{
                        height: isMobile ? "34px" : "37.69px",
                        fontSize: isMobile ? "0.85rem" : "1rem",
                        width: isMobile ? "150%" : 185,
                      }}
                    >
                      <MenuItem value="All">Select Status</MenuItem>
                      <MenuItem value="true">Active</MenuItem>
                      <MenuItem value="false">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Box
                display="flex"
                flexDirection={isMobile ? "column" : "row"}
                justifyContent="left"
                gap={isMobile ? 1 : 2}
                mt={isMobile ? 2 : 4}
              >
                <Button
                  type="submit"
                  sx={{
                    background: Color.primary.main,
                    color: "white",
                    height: isMobile ? "34px" : "37.69px",
                    width: isMobile ? "100%" : 120,
                    fontSize: isMobile ? "0.85rem" : "1rem",
                    "&:hover": {
                      background: Color.primary.dark,
                    },
                  }}
                  startIcon={<SearchIcon />}
                >
                  Search
                </Button>

                <Button
                  onClick={handleReset}
                  sx={{
                    background: "none",
                    color: Color.secondary.main,
                    border: "1px solid",
                    borderColor: Color.secondary.main,
                    height: isMobile ? "34px" : "37.69px",
                    width: isMobile ? "100%" : 120,
                    fontSize: isMobile ? "0.85rem" : "1rem",
                  }}
                >
                  Reset
                </Button>

                <Button
                  onClick={() => setDrawerOpen(false)}
                  sx={{
                    background: "none",
                    color: Color.secondary.main,
                    border: "1px solid",
                    borderColor: Color.secondary.main,
                    height: isMobile ? "34px" : "37.69px",
                    width: isMobile ? "100%" : 120,
                    fontSize: isMobile ? "0.85rem" : "1rem",
                  }}
                >
                  Close
                </Button>
              </Box>
            </form>
          </Box>
        </Box>
      </Drawer>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            width: isMobile ? "90vw" : "auto",
            m: isMobile ? 1 : 2,
          },
        }}
      >
        <DialogTitle sx={{ fontSize: isMobile ? "1rem" : "1.25rem" }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: isMobile ? "0.85rem" : "1rem" }}>
            Are you sure you want to delete the product{" "}
            <strong>{selectedRow?.name ?? "this product"}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            mr: 2,
            mb: 2,
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? 1 : 0,
          }}
        >
          <Button
            onClick={handleCancelDelete}
            sx={{
              border: "1px solid",
              borderColor: Color.secondary.main,
              color: Color.label.main,
              width: isMobile ? "100%" : "auto",
              fontSize: isMobile ? "0.85rem" : "1rem",
              padding: isMobile ? "8px" : "6px 16px",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            sx={{
              background: Color.primary.main,
              color: "white",
              width: isMobile ? "100%" : "auto",
              fontSize: isMobile ? "0.85rem" : "1rem",
              padding: isMobile ? "8px" : "6px 16px",
              "&:hover": {
                background: Color.primary.dark,
              },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <AddProduct
        open={openAddModal}
        onClose={() => setOpenAddModal(false)}
        onSuccess={() => {
          setOpenAddModal(false);
          fetchProducts({ page: paginationModel.page, pageSize: paginationModel.pageSize });
        }}
      />

      <EditProduct
        open={openEditModal}
        onClose={() => setOpenEditModal(false)}
        productId={selectedId}
        onUpdate={() => {
          setOpenEditModal(false);
          fetchProducts({ page: paginationModel.page, pageSize: paginationModel.pageSize });
        }}
      />
    </Box>
  );
};

export default Products;